import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, AlertTriangleIcon, ChevronRightIcon, DropletIcon, FilePlus2Icon, BarChart3Icon, SearchCheckIcon, SettingsIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
import { WATER_HERO_IMAGES as IMG, waterHeroCopy as c, waterJourney, type Bi } from '../../data/waterHero';
import { WaterLaptop } from './WaterLaptop';
const EASE = [0.23, 1, 0.32, 1] as const;
const JOURNEY_ICONS = {
  register: FilePlus2Icon,
  measure: BarChart3Icon,
  detect: SearchCheckIcon,
  act: SettingsIcon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 18%, #000 38%)',
  maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 18%, #000 38%)'
};
type P = {
  isFR: boolean;
};
function AlertCard({
  isFR
}: P) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div className="flex items-center gap-3.5 rounded-2xl bg-white border border-[#EEEEF4] px-4 py-3.5 shadow-[0_20px_40px_-26px_rgba(30,30,80,0.45)]" data-id="element-7531" data-mp="2smu5vd5ylqaw">
      <span className="w-11 h-11 rounded-full bg-[#FDECEC] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7532" data-mp="32l9qcxahe1bf">
        <AlertTriangleIcon className="w-5 h-5 text-[#E5484D]" strokeWidth={2} data-id="element-7533" data-mp="2xkvqi2j19xoh" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-7534" data-mp="3bkd89xxwwaoc">
        <span className="block font-sans text-[15px] font-semibold text-[#121A3A]" data-id="element-7535" data-mp="3bjbu3klln82e">{pick(c.alertTitle)}</span>
        <span className="block font-sans text-[13px] text-[#5B6478]" data-id="element-7536" data-mp="37i0bhz157n3p">{pick(c.alertSub)}</span>
        <span className="block font-sans text-[13px] text-[#5B6478]" data-id="element-7537" data-mp="3ld2hsn9wjeh1">{pick(c.alertAddress)}</span>
      </span>
      <ChevronRightIcon className="w-4 h-4 text-[#121A3A] shrink-0" aria-hidden="true" data-id="element-7538" data-mp="3v9fabhtpnma7" />
    </div>;
}
function HouseBubble({
  isFR,
  size = 'lg'
}: P & {
  size?: 'lg' | 'sm';
}) {
  const dim = size === 'lg' ? 'w-[170px] h-[170px]' : 'w-[96px] h-[96px]';
  return <div className={`relative ${dim} shrink-0`} data-id="element-7539" data-mp="19polunei0q1">
      <img src={IMG.house} alt={isFR ? c.houseAlt.fr : c.houseAlt.en} className="w-full h-full rounded-full object-cover border-[5px] border-white shadow-[0_20px_40px_-24px_rgba(20,20,60,0.5)]" data-id="element-7540" data-mp="66ojwgiswdpt" />
      <span className="absolute -bottom-1 -right-1 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center" aria-hidden="true" data-id="element-7541" data-mp="fwrcwgpx9ro5">
        <DropletIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} data-id="element-7542" data-mp="kvkmvz6030jk" />
      </span>
    </div>;
}
export function WaterHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const message = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-7543" data-mp="yw55ovbx0wd2">{pick(c.eyebrow)}</p>
      <h1 className="font-serif text-[3rem] sm:text-[4.2rem] 2xl:text-[5.4rem] leading-[0.98] tracking-tight mt-7" data-id="element-7544" data-mp="2vpgrx0kdd6y6">
        {c.navy.map(l => <span key={l.en} className="block text-[#0E1330]" data-id="element-7545" data-mp="2t3qyqcs4fv3p">{pick(l)}</span>)}
        {c.indigo.map(l => <span key={l.en} className="block text-[#4F46E5]" data-id="element-7546" data-mp="2och7x8uu6lq8">{pick(l)}</span>)}
      </h1>
      <p className="font-sans text-[18px] 2xl:text-[20px] leading-[1.55] text-[#4A5470] mt-8 max-w-[500px]" data-id="element-7547" data-mp="2jdeeyms4s5ym">{pick(c.body)}</p>
      <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-7548" data-mp="2efw6sylxturo">
        <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
        location: 'Smart Water Hero'
      })} className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-[#4F46E5] px-8 py-4 font-sans text-[16px] font-medium text-white whitespace-nowrap shadow-[0_14px_30px_-14px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-7549" data-mp="3c34vt3l5dlyf">
          {pick(c.primary)}
          <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-7550" data-mp="1qq1t6etso26d" />
        </Link>
        <a href="#casselman-proof" onClick={e => {
        e.preventDefault();
        document.getElementById('casselman-proof')?.scrollIntoView({
          behavior: 'smooth'
        });
        track('Proof CTA Clicked', {
          location: 'Smart Water Hero'
        });
      }} className="inline-flex items-center justify-center gap-2.5 rounded-xl border border-[#DCDDE6] bg-white px-8 py-4 font-sans text-[16px] font-medium text-[#121A3A] whitespace-nowrap hover:border-[#4F46E5] transition-colors duration-150" data-id="element-7551" data-mp="1lpe43yp4lves">
          {pick(c.secondary)}
          <ArrowRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-7552" data-mp="2530v5iyln0u" />
        </a>
      </div>
    </>;
  return <section className="relative overflow-hidden bg-white" data-id="element-7553" data-mp="3jbveuccrilvw">
      {/* Desktop composition */}
      <div className="hidden xl:block relative min-h-[800px]" data-id="element-7554" data-mp="3oagfgcvxf7kv">
        <div className="absolute inset-y-0 right-0 w-[74%]" aria-hidden="true" data-id="element-7555" data-mp="27xrp5id7jjwn">
          <img src={IMG.background} alt="" className="w-full h-full object-cover object-center" style={PHOTO_MASK} data-id="element-7556" data-mp="2cwl136cuj2up" />
        </div>

        <div className="relative max-w-[1760px] mx-auto h-full min-h-[800px]" data-id="element-7557" data-mp="uy6l0f6sjph6">
          {/* Dotted connectors */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1760 800" preserveAspectRatio="none" aria-hidden="true" data-id="element-7558" data-mp="pytpl61uta2z">
            <path d="M1320 190 L1320 250" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7559" data-mp="13xbf99bbkwnj" />
            <path d="M1478 130 L1520 150" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7560" data-mp="zw7z0lpd0yls" />
            <path d="M1600 300 L1600 380" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7561" data-mp="c642szei414t" />
            <path d="M1450 440 L1530 440" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7562" data-mp="77keiscfqd9a" />
          </svg>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.15,
          ease: EASE
        }} className="absolute z-10 top-[88px] left-[68%] w-[290px]" data-id="element-7563" data-mp="gabosotkr2zc">
            <AlertCard isFR={isFR} data-id="element-7564" data-mp="3ko1k3nbfpvhx" />
          </motion.div>

          <div className="absolute z-10 top-[96px] right-[3%]" data-id="element-7565" data-mp="1nofetrxa5kji">
            <HouseBubble isFR={isFR} data-id="element-7566" data-mp="1smqq8738433u" />
          </div>

          <img src={IMG.meter} alt={pick(c.meterAlt)} className="absolute z-[5] top-[300px] right-[1%] w-[230px] h-auto mix-blend-multiply" data-id="element-7567" data-mp="1slfjpzie8h91" />

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.08,
          ease: EASE
        }} className="absolute z-10 top-[235px] left-[37%] w-[46%] max-w-[820px]" data-id="element-7568" data-mp="22bifg7gwg9b2">
            <WaterLaptop isFR={isFR} data-id="element-7569" data-mp="1emfyz2y04mi5" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="relative z-20 w-[36%] pl-6 2xl:pl-12 pt-20 pb-60" data-id="element-7570" data-mp="1ep25cz95ovys">
            {message}
          </motion.div>
        </div>

        <svg className="absolute bottom-0 left-0 w-full h-[190px] pointer-events-none z-20" viewBox="0 0 1440 190" preserveAspectRatio="none" aria-hidden="true" data-id="element-7571" data-mp="2v323q5scsdjx">
          <path d="M0 190 L0 60 C 300 30, 520 120, 860 120 C 1120 120, 1300 70, 1440 20 L1440 190 Z" fill="#FFFFFF" data-id="element-7572" data-mp="eg3uysnxwx84" />
        </svg>
      </div>

      {/* Tablet / mobile */}
      <div className="xl:hidden px-6 pt-12 max-w-[1100px] mx-auto" data-id="element-7573" data-mp="4prat1k1f62i">
        {message}
        <div className="relative mt-10 rounded-[24px] overflow-hidden" data-id="element-7574" data-mp="3vvscwkm0ypc0">
          <img src={IMG.background} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" aria-hidden="true" data-id="element-7575" data-mp="3vsxuszqhhglr" />
          <div className="relative p-4 sm:p-6" data-id="element-7576" data-mp="3mu2n1j7sjygc">
            <WaterLaptop isFR={isFR} data-id="element-7577" data-mp="3muuj0v5avy75" />
          </div>
        </div>
        <div className="mt-6 flex items-center gap-4 sm:gap-6" data-id="element-7578" data-mp="3hwj8ei76ljnv">
          <HouseBubble isFR={isFR} size="sm" data-id="element-7579" data-mp="1l2zb8qm60du7" />
          <img src={IMG.meter} alt={pick(c.meterAlt)} className="w-[88px] h-auto mix-blend-multiply shrink-0" data-id="element-7580" data-mp="1l1wied5a81bn" />
          <div className="flex-1 min-w-0 hidden sm:block" data-id="element-7581" data-mp="1g4xrs4ktuci7">
            <AlertCard isFR={isFR} data-id="element-7582" data-mp="oyin76s4g2uo" />
          </div>
        </div>
        <div className="mt-4 sm:hidden" data-id="element-7583" data-mp="p14xvfab6yh4">
          <AlertCard isFR={isFR} data-id="element-7584" data-mp="twtxfimbwfdf" />
        </div>
      </div>

      {/* Four-stage journey */}
      <ol className="relative z-30 max-w-[1500px] mx-auto px-6 pt-14 xl:-mt-24 pb-16 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-y-10 gap-x-6 items-center" data-id="element-7585" data-mp="f9r0pe5tyar5">
        {waterJourney.map((s, i) => {
        const Icon = JOURNEY_ICONS[s.key];
        return <Fragment key={s.key} data-id="element-7586" data-mp="b7nkydc4ok02" data-mpi={i}>
              <li className="flex items-center gap-5" data-id="element-7587" data-mp="b8p1uyfts0pp">
                <span className="w-[76px] h-[76px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7588" data-mp="3sganr9kbpv4h">
                  <Icon className="w-8 h-8 text-[#4F46E5]" strokeWidth={1.6} data-id="element-7589" data-mp="19g7w5kymm3c" />
                </span>
                <span data-id="element-7590" data-mp="1qoxhag2pjf6b">
                  <span className="block font-sans text-[15px] font-bold uppercase tracking-[0.16em] text-[#1E2A6B]" data-id="element-7591" data-mp="1vn8t2wckbzr6">{pick(s.label)}</span>
                  <span className="block font-sans text-[15px] leading-snug text-[#5B6478] mt-1.5 max-w-[220px]" data-id="element-7592" data-mp="3g6b880vkhpjo">{pick(s.text)}</span>
                </span>
              </li>
              {i < waterJourney.length - 1 && <li className="hidden xl:flex justify-center" aria-hidden="true" data-id="element-7593" data-mp="3pwnpnk8352ll">
                  <ArrowRightIcon className="w-5 h-5 text-[#9AA0C8]" strokeWidth={1.6} data-id="element-7594" data-mp="3l3ku56jqzj2r" />
                </li>}
            </Fragment>;
      })}
      </ol>
    </section>;
}